'use client';

import { DEFAULT_DOMAIN } from '@/lib/constants';
import {
  generateBreadcrumbStructuredData,
  validateBreadcrumbItems,
} from '@/lib/structured-data/generators';
import { StructuredData } from '@/lib/structured-data/StructuredData';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styles from './breadcrumb.module.scss';
import { cn } from '@/lib/utils';

export type BreadcrumbItemData = {
  label: string;
  href: string;
};

export interface BreadcrumbProps {
  items?: BreadcrumbItemData[];
  enableStructuredData?: boolean;
  baseUrl?: string;
}

function buildCrumbsFromPathname(pathname: string): BreadcrumbItemData[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  return [
    { label: 'Home', href: '/' },
    ...segments.map((segment, idx) => {
      const href = '/' + segments.slice(0, idx + 1).join('/');
      const raw = decodeURIComponent(segment).replace(/-/g, ' ');
      const lc = raw.toLowerCase();
      const label = lc
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return { label, href };
    }),
  ];
}

export const Breadcrumb = ({
  items,
  enableStructuredData = true,
  baseUrl = DEFAULT_DOMAIN,
}: BreadcrumbProps = {}) => {
  const pathname = usePathname();

  // Handle null pathname (e.g., in Storybook environment)
  if (!pathname || pathname === '/') return null;

  const crumbs = items && items.length > 0 ? items : buildCrumbsFromPathname(pathname);

  if (crumbs.length === 0) return null;

  // Generate structured data for breadcrumbs - Server Component compatible
  const shouldIncludeStructuredData = enableStructuredData && validateBreadcrumbItems(crumbs);
  const breadcrumbStructuredData = shouldIncludeStructuredData
    ? generateBreadcrumbStructuredData(crumbs, baseUrl)
    : null;

  return (
    <>
      {/* Include breadcrumb structured data for SEO */}
      {breadcrumbStructuredData && (
        <StructuredData
          data={breadcrumbStructuredData}
          id={`breadcrumb-structured-data-${pathname.replace(/\//g, '-')}`}
        />
      )}
      <nav aria-label="breadcrumb">
        <ol className={styles.breadcrumb}>
          {crumbs.map((crumb, idx) => {
            const isLast = idx === crumbs.length - 1;
            return (
              <li key={crumb.href} className="flex items-center">
                {isLast ? (
                  // Last item: plain text, active style
                  <span className={cn(styles.breadcrumb__link, styles['breadcrumb__link--active'])}>
                    {crumb.label}
                  </span>
                ) : (
                  // Intermediate items: clickable Link
                  <Link href={crumb.href} className={styles.breadcrumb__link}>
                    {crumb.label}
                  </Link>
                )}

                {/* Separator, except after the last crumb */}
                {!isLast && (
                  <ChevronRight
                    size={16}
                    strokeWidth={2}
                    className={styles.breadcrumb__separator}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};
